import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { signupEnabled } from "../../login/authConfig";
import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale == null) {
        throw new Error();
      }

      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

const getSale = async () => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    return JSON.parse(cachedValue);
  }

  // A failed read must not throw. canAccess treats a throw as an
  // authentication error and leaves the list, so an offline reload would
  // never reach the connection alert.
  try {
    const { data: dataSession, error: errorSession } =
      await getSupabaseClient().auth.getSession();

    // Shouldn't happen after login but just in case
    if (dataSession?.session?.user == null || errorSession) {
      return undefined;
    }

    const { data: dataSale, error: errorSale } = await getSupabaseClient()
      .from("sales")
      .select("id, first_name, last_name, avatar, administrator")
      .match({ user_id: dataSession?.session?.user.id })
      .single();

    // Shouldn't happen either as all users are sales but just in case
    if (dataSale == null || errorSale) {
      return undefined;
    }

    storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
    return dataSale;
  } catch {
    return undefined;
  }
};

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
}

// Pause before asking again. A Vernieuwen reload can find the auth lock busy:
// getSession() then throws a lock timeout although the stored token is fine.
const SESSION_RETRY_DELAY_MS = 300;

type StoredSession = { access_token?: string };

const isServerAuthStatus = (status: unknown) =>
  status === 401 || status === 403;

function readStoredSession(): StoredSession | null {
  const storage = getLocalStorage();
  const key = (getSupabaseClient().auth as unknown as { storageKey?: string })
    .storageKey;
  if (!storage || !key) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

/**
 * Called after a session check failed on the client. True while the stored
 * token is still accepted, so only the auth server can end the session.
 */
export async function sessionSurvives(
  wait: (ms: number) => Promise<void> = (ms) =>
    new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<boolean> {
  const stored = readStoredSession();
  if (!stored?.access_token) return false;
  await wait(SESSION_RETRY_DELAY_MS);
  try {
    const { data } = await getSupabaseClient().auth.getSession();
    if (data?.session != null) return true;
  } catch {
    // Same lock problem again. Ask the server with the stored token.
  }
  const token = readStoredSession()?.access_token;
  if (!token) return false;
  try {
    // With an explicit token getUser skips the lock and asks the server.
    const { data, error } = await getSupabaseClient().auth.getUser(token);
    if (data?.user != null) return true;
    // Only a real auth answer ends the session. A network failure does not.
    return !(error && isServerAuthStatus(error.status));
  } catch {
    return true;
  }
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      const onSignupPath =
        window.location.pathname === "/sign-up" ||
        window.location.pathname === "/signup" ||
        window.location.hash.includes("#/sign-up") ||
        window.location.hash.includes("#/signup");
      if (onSignupPath) {
        if (!signupEnabled) {
          // Hash routing only rewrites the hash, so /sign-up#/login would
          // keep signup in the address bar. Send the browser to /#/login.
          const next = new URL(window.location.href);
          if (next.pathname !== "/" || !next.hash.startsWith("#/login")) {
            next.pathname = "/";
            next.hash = "#/login";
            next.search = "";
            window.location.replace(next.toString());
          }
          throw {
            redirectTo: "/login",
            message: false,
          };
        }
        return;
      }

      if (signupEnabled) {
        const isInitialized = await getIsInitialized();

        if (!isInitialized) {
          await getSupabaseClient().auth.signOut();
          throw {
            redirectTo: "/sign-up",
            message: false,
          };
        }
      }

      try {
        return await baseAuthProvider.checkAuth(params);
      } catch (error) {
        // A client-side miss (lock timeout, storage read) is not a logout.
        if (await sessionSurvives()) return;
        throw error;
      }
    },
    checkError: async (error) => {
      // Only a server answer logs out. A failed fetch in the browser has no
      // status and stays a normal error.
      if (isServerAuthStatus(error?.status)) {
        return Promise.reject();
      }
      if (error?.name === "AuthSessionMissingError") {
        if (await sessionSurvives()) return;
        return Promise.reject();
      }
    },
    canAccess: async (params) => {
      // Signup-off login does not require init_state. Returning false here
      // sends Create/Show to ra-core's blank /access-denied page.
      if (signupEnabled && !(await getIsInitialized())) return false;

      const sale = await getSale();
      if (sale == null && signupEnabled) return false;

      const role = sale?.administrator ? "admin" : "user";
      return canAccess(role, params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
